import { useContext, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Chip, DialogActions, Skeleton } from '@mui/material';
import { File as DBFile } from '@esg/esg-global-types';
import { MetricRecordFilters } from '@esg/syntiro-api-core';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { MetricRecord, ReportingPeriodGroup } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import { ReactComponent as CopyIcon } from '../../../assets/icons/Paste.svg';
import { QualitativeMetricRecordGridRow } from './QualitativeMetricRecordGrid';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { BodyText, MinorHeading, Subheading } from '../../shared/typography/PrimaryTypography';
import ReportingPeriodGroupSelect from '../../shared/input/select/ReportingPeriodGroupSelect';
import AlertBanner from '../../shared/banner/AlertBanner';
import FilledContainer from '../../shared/container/FilledContainer';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import {
  MetricRecordEntitiesMap,
  getMetricRecords
} from '../../../graphql/services/metric_record.service';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';
import { GET_METRIC_RECORDS_QUALITATIVE } from '../../../graphql/queries/metric_record.queries';
import {
  getFilesForMetricRecords,
  createFileForMetricRecord,
  deleteFileForMetricRecord
} from '../../../lib/metric_capture/file';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

interface CopyQualitativeMetricRecordModalProps {
  open: boolean;
  metric_record: QualitativeMetricRecordGridRow | null;
  entities_map: MetricRecordEntitiesMap;
  capture_parameters: MetricRecordCaptureParameters;
  handleUpdateRow: (
    old_metric_record: QualitativeMetricRecordGridRow,
    new_metric_record: QualitativeMetricRecordGridRow
  ) => Promise<QualitativeMetricRecordGridRow | undefined>;
  handleCloseCopyModal: () => void;
  handleAddSupportingDoc: (row_id: string, new_doc: DBFile) => void;
  handleRemoveSupportingDoc: (row_id: string, deleted_doc_id: string) => void;
}

/**
 * A modal allowing the user to copy a qualitative Metric Record's status and value from another Reporting Period Group.
 * @param {boolean} open The display state of the modal.
 * @param {QualitativeMetricRecordGridRow | null} metric_record The metric record to copy to.
 * @param {MetricRecordEntitiesMap} entities_map Options for the selected Reporting Period Group.
 * @param {MetricRecordCaptureParameters} capture_parameters Data for the updating of the copied Metric Record.
 * @param {Promise<QualitativeMetricRecordGridRow | undefined>} handleUpdateRow Function to update the chosen Metric Record with the copied data.
 * @param {void} handleCloseCopyModal Handler function to close the modal.
 * @param {void} handleAddSupportingDoc Handler to add the copied document to the supportingDocs state in QualitativeMetricRecordGrid.
 * @param {void} handeRemoveSupportingDoc Handler to remove the document from the supportingDocs state if it gets removed during a copy.
 * @returns {JSX.Element}
 */
const CopyQualitativeMetricRecordModal = ({
  open,
  metric_record,
  entities_map,
  capture_parameters,
  handleUpdateRow,
  handleCloseCopyModal,
  handleAddSupportingDoc,
  handleRemoveSupportingDoc
}: CopyQualitativeMetricRecordModalProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [copyMetricRecord, setCopyMetricRecord] = useState<MetricRecord | null | undefined>(
    undefined
  );
  const [copyLoading, setCopyLoading] = useState<boolean>(false);
  const [copyMetricRecordLoading, setCopyMetricRecordLoading] = useState<boolean>(false);
  const [copyMetricRecordFiles, setCopyMetricRecordFiles] = useState<Array<DBFile>>([]);

  const available_reporting_period_groups: Array<ReportingPeriodGroup> = Object.values(
    entities_map.reporting_period_groups
  ).filter((reporting_period_group: ReportingPeriodGroup) => {
    return reporting_period_group.id !== capture_parameters.reporting_period_group?.id;
  });

  const copyMetricRecordFromReportingPeriodGroup = async (): Promise<void> => {
    setCopyLoading(true);
    try {
      if (
        metric_record &&
        copyMetricRecord &&
        copyMetricRecord.status !== undefined &&
        group &&
        company
      ) {
        const new_metric_record: QualitativeMetricRecordGridRow = {
          ...metric_record,
          status: copyMetricRecord.status,
          value: copyMetricRecord.value.toString()
        };
        const updated_row: QualitativeMetricRecordGridRow | undefined = await handleUpdateRow(
          metric_record,
          new_metric_record
        );
        if (!updated_row) return;
        if (copyMetricRecordFiles.length === 0) {
          // If the copied record contains no document, and you're copying it over to a value with a document, delete the document.
          const old_files: Array<DBFile> = await getFilesForMetricRecords(
            group.id,
            company.id,
            updated_row.id
          );
          if (old_files && old_files.length !== 0) {
            for (const file of old_files) {
              await deleteFileForMetricRecord(group.id, company.id, file);
              handleRemoveSupportingDoc(updated_row.id, file.id);
            }
          } else return;
        }
        // Create new file(s) for the new metric record.
        for (const file of copyMetricRecordFiles) {
          await createFileForMetricRecord(
            group.id,
            company.id,
            file.name,
            updated_row.id,
            file.file_path
          ).then((supporting_doc: DBFile) => {
            const copied_file: DBFile = {
              id: supporting_doc.id,
              deleted: null,
              file_path: supporting_doc.file_path,
              metric_record: supporting_doc.metric_record,
              name: supporting_doc.name,
              type: `.${supporting_doc.name.split('.')[1].toLowerCase()}`,
              uploaded: new Date()
            };
            // Add to supporting docs.
            handleAddSupportingDoc(updated_row.id, copied_file);
          });
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(
                'CopyQualitativeMetricRecordModal.tsx',
                'copyMetricRecordFromReportingPeriodGroup'
              ),
          {
            group: group,
            company: company,
            old_metric_record: metric_record,
            new_metric_record: copyMetricRecord
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update metric record. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setCopyLoading(false);
    }
  };

  const findMetricRecordToCopy = async (
    reporting_period_group: ReportingPeriodGroup
  ): Promise<void> => {
    setCopyMetricRecordLoading(true);
    try {
      if (group && company && metric_record) {
        const metric_record_filters: MetricRecordFilters = {
          reporting_period_group_id: reporting_period_group.id,
          metric_group_id: metric_record.metric.metric_group.id,
          metric_id: metric_record.metric.id
        };
        const copy_metric_record: Array<MetricRecord> | null = await getMetricRecords(
          { group_id: group.id, company_id: company.id },
          metric_record_filters,
          GET_METRIC_RECORDS_QUALITATIVE
        );
        const record: MetricRecord | null = copy_metric_record?.[0] ?? null;
        const copy_files: Array<DBFile> = record
          ? await getFilesForMetricRecords(group.id, company.id, record.id)
          : [];
        setCopyMetricRecordFiles(copy_files ?? []);
        setCopyMetricRecord(record);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: CopyQualitativeMetricRecordModal failed on an unknown error while calling findMetricRecordToCopy.',
          {
            group: group,
            company: company,
            reporting_period_group: reporting_period_group
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update metric record. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setCopyMetricRecordLoading(false);
    }
  };

  const handleCloseModal = (): void => {
    setCopyMetricRecord(undefined);
    setCopyMetricRecordFiles([]);
    handleCloseCopyModal();
  };

  return (
    <Dialog open={open} onClose={handleCloseModal}>
      <Subheading weight="Bold" sx={{ m: 2 }}>
        Copy Metric Record from Reporting Period Group
      </Subheading>
      <DialogContent>
        <>
          <MinorHeading weight="Medium" sx={{ mb: 4, mt: 2 }}>
            {metric_record?.metric.name}
          </MinorHeading>
          <ReportingPeriodGroupSelect
            reporting_period_group_options={available_reporting_period_groups}
            handleChangeReportingPeriodGroups={async (
              reporting_period_group: Array<ReportingPeriodGroup> | ReportingPeriodGroup | null
            ) => {
              if (reporting_period_group) {
                await findMetricRecordToCopy(reporting_period_group as ReportingPeriodGroup);
              }
            }}
          />
          {copyMetricRecordLoading ? (
            <Skeleton sx={{ my: 4, height: 75, backgroundColor: '#7D8F99' }} />
          ) : (
            <>
              {copyMetricRecord === null && (
                <Box sx={{ my: 4 }}>
                  <AlertBanner
                    severity="info"
                    open
                    message={`No Existing Metric Record found for selected Reporting Period Group`}
                  />
                </Box>
              )}
              {copyMetricRecord && (
                <Box sx={{ my: 4 }}>
                  <Box sx={{ display: 'flex' }}>
                    <MinorHeading weight="Medium">Found Metric Record</MinorHeading>
                    <ExpandMore color="primary" />
                  </Box>
                  <FilledContainer
                    sx={{
                      my: 2,
                      p: 4
                    }}
                  >
                    <MinorHeading weight="Medium" sx={{ mb: 2 }}>
                      Compliant
                    </MinorHeading>
                    <Chip
                      sx={{ my: 2 }}
                      label={copyMetricRecord.status ? 'True' : 'False'}
                      color={copyMetricRecord.status ? 'success' : 'error'}
                      variant="outlined"
                      icon={
                        copyMetricRecord.status ? (
                          <CheckCircleOutlineOutlinedIcon />
                        ) : (
                          <HighlightOffOutlinedIcon />
                        )
                      }
                    />
                    <MinorHeading weight="Medium" sx={{ my: 2 }}>
                      Details
                    </MinorHeading>
                    <BodyText>{copyMetricRecord.value}</BodyText>
                    <MinorHeading weight="Medium" sx={{ my: 2 }}>
                      Supporting Document(s)
                    </MinorHeading>
                    <BodyText sx={{ whiteSpace: 'pre-line' }}>
                      {copyMetricRecordFiles.map((file) => file.name).join(',\n') ||
                        'No documents found.'}
                    </BodyText>
                  </FilledContainer>
                </Box>
              )}
            </>
          )}
        </>
        <DialogActions sx={{ mt: 4 }}>
          <GhostButton dark onClick={handleCloseModal}>
            Cancel
          </GhostButton>
          <PrimaryButton
            startIcon={<CopyIcon height={30} fill="currentColor" stroke="currentColor" />}
            loading={copyLoading}
            onClick={async () => {
              await copyMetricRecordFromReportingPeriodGroup();
              handleCloseModal();
            }}
            disabled={!copyMetricRecord}
          >
            Copy Metric Record
          </PrimaryButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default CopyQualitativeMetricRecordModal;
